import React, { useCallback, useEffect, useState, useMemo } from 'react'
import { X } from 'lucide-react'
import { SelectionModal } from '../../../components/layout/SelectionModal'
import { UserFile } from '../../../service/user'
import { useImageSelection } from '../tabs/StyleImageTab/hooks/useImageSelection'
import { useImageUpload } from '../tabs/StyleImageTab/hooks/useImageUpload'
import { ImageGrid } from '../tabs/StyleImageTab/components/ImageGrid'
import { ImageCard } from '../tabs/StyleImageTab/components/ImageCard'
import { SampleFile, SampleResponse } from 'service/aiStudio'
import { SampleImageCard } from 'pages/ai-studio/tabs/StyleImageTab/components/SampleImageCard'
import classNames from 'classnames'
import { isMobile } from 'react-device-detect'
import InfiniteScroll from 'react-infinite-scroll-component'
import { BeatLoader } from 'react-spinners'
import { useInfiniteSampleByGarment } from '../../../hooks/query/aiStudio/useInfiniteSampleByGarment'

interface SampleSelectionModalProps {
  isOpen: boolean
  garmentType: string
  onClose: () => void
  onSample: (file: SampleFile[]) => void
}

export const SampleSelectionModal: React.FC<SampleSelectionModalProps> = ({
  isOpen,
  onClose,
  garmentType,
  onSample,
}) => {
  // 무한 스크롤 데이터 가져오기
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, status } =
    useInfiniteSampleByGarment({
      garmentType,
      limit: 20,
      enabled: isOpen && !!garmentType,
    })

  // 모든 페이지의 데이터를 하나의 배열로 합치기
  const allSampleFiles = useMemo(() => {
    if (!data?.pages) return []
    return data.pages.flatMap((page) => page.items)
  }, [data])
  // 커스텀 훅 사용

  const [selectedFiles, setSelectedFiles] = useState<SampleFile[]>([])
  const handleImageClick = (file: SampleFile) => {
    const isSelectedFile = selectedFiles.find((el) => el.id === file.id)
    if (isSelectedFile) {
      //파일이 있음 제거
      const updateSelectedFiles = selectedFiles.filter(
        (el) => el.id !== file.id,
      )
      setSelectedFiles(updateSelectedFiles)
    } else {
      if (selectedFiles.length === 1 && file) {
        setSelectedFiles([file])
        return
      } else {
        //파일 추가
        setSelectedFiles((prev) => {
          return [...prev, file]
        })
      }
    }
  }

  const handleSave = useCallback(() => {
    if (selectedFiles.length > 0 && selectedFiles.length < 2) {
      onSample(selectedFiles)
      onClose()
    }
  }, [selectedFiles, onClose])

  useEffect(() => {
    setSelectedFiles([])
  }, [garmentType])

  if (!isOpen) return null

  return (
    <SelectionModal isModal={isOpen}>
      <div
        className={classNames('', {
          'flex h-[600px] w-[816px] flex-col overflow-hidden rounded-[20px] border border-[#EEF1F7] bg-white shadow-[0px_0px_20px_0px_rgba(22,27,35,0.1)]':
            !isMobile,
          'flex h-[100%] w-full flex-col overflow-auto bg-white': isMobile,
        })}
      >
        {/* 헤더 */}
        <div className="flex-shrink-0 px-10 pb-6 pt-10">
          <div className="flex justify-between md:items-center">
            <div className="flex flex-col items-start">
              <h2 className="text-[24px] font-bold text-[#2E343D]">
                {garmentType === 'bottom' ? '하의 샘플' : '상의 샘플'} 선택
              </h2>
              <h3 className="text-left">함께 입을 옷은 1개 선택 가능합니다.</h3>
            </div>

            <button
              onClick={onClose}
              className="flex h-6 w-6 items-center justify-center"
            >
              <X size={24} className="text-black" />
            </button>
          </div>
        </div>

        {/* 이미지 그리드 - 무한 스크롤 영역 */}
        <div
          className="flex-1 justify-center overflow-y-auto px-8"
          id="sample-scroll-container"
        >
          <InfiniteScroll
            dataLength={allSampleFiles.length}
            next={fetchNextPage}
            hasMore={!!hasNextPage}
            loader={
              <div className="flex justify-center py-4">
                <BeatLoader color="#00B7B9" size={8} />
              </div>
            }
            endMessage={
              <div className="py-4 text-center text-gray-500">
                모든 샘플을 불러왔습니다.
              </div>
            }
            scrollableTarget="sample-scroll-container"
            className=""
          >
            <div className="flex flex-wrap justify-between gap-2 pb-6 pt-2 md:justify-start">
              {allSampleFiles.map((file) => {
                const isSelected = selectedFiles.find((el) => el.id === file.id)
                return (
                  <SampleImageCard
                    file={file}
                    key={file.id}
                    src={file.thumbnailUrl}
                    isSelected={isSelected ? true : false}
                    onClick={handleImageClick}
                  />
                )
              })}
            </div>
          </InfiniteScroll>
        </div>

        {/* 하단 저장 버튼 */}
        <div className="flex-shrink-0 border-t-[1px] border-[#EEF1F7] bg-white p-5 px-10 shadow-[0px_-4px_10px_0px_rgba(0,0,0,0.1)]">
          <button
            onClick={handleSave}
            disabled={selectedFiles.length === 0 || selectedFiles.length > 4}
            className={`flex h-[48px] w-full items-center justify-center rounded-[60px] text-[18px] font-bold transition-colors ${
              selectedFiles.length > 0 && selectedFiles.length < 5
                ? 'bg-[#00B7B9] text-white hover:bg-[#00A5A7]'
                : 'cursor-not-allowed bg-gray-300 text-gray-500'
            }`}
          >
            저장
          </button>
        </div>
      </div>
    </SelectionModal>
  )
}
